import { ScrollView, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { TYPO } from '@/constants/typography';
import { RADIUS, FONT, TRACKING, SPACE } from '@/constants/designTokens';
import type { UserGym } from '@/hooks/useUserGyms';

interface Props {
  gyms: UserGym[];
  activeGymId: string | null;
  onSelect: (gym: UserGym) => void;
  colors: Record<string, string>;
  /** If true, show an "All Gyms" option at the start */
  showAll?: boolean;
  allSelected?: boolean;
  onSelectAll?: () => void;
}

export default function GymSwitcherPills({
  gyms,
  activeGymId,
  onSelect,
  colors: c,
  showAll,
  allSelected,
  onSelectAll,
}: Props) {
  if (gyms.length < 2 && !showAll) return null;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.row}
    >
      {showAll && onSelectAll && (
        <TouchableOpacity
          style={[
            styles.pill,
            { borderColor: c.border },
            allSelected && { backgroundColor: '#B8972E', borderColor: '#B8972E' },
          ]}
          onPress={onSelectAll}
          accessibilityRole="button"
          accessibilityLabel="All Gyms"
          accessibilityState={{ selected: allSelected }}
        >
          <Text
            style={[
              styles.pillText,
              { color: allSelected ? '#030303' : c.textSecondary },
            ]}
          >
            All Gyms
          </Text>
        </TouchableOpacity>
      )}
      {gyms.map((gym) => {
        const selected = !allSelected && gym.id === activeGymId;
        return (
          <TouchableOpacity
            key={gym.id}
            style={[
              styles.pill,
              { borderColor: c.border },
              selected && { backgroundColor: '#B8972E', borderColor: '#B8972E' },
            ]}
            onPress={() => onSelect(gym)}
            accessibilityRole="button"
            accessibilityLabel={gym.name}
            accessibilityState={{ selected }}
          >
            <Text
              style={[
                styles.pillText,
                { color: selected ? '#030303' : c.textSecondary },
              ]}
              numberOfLines={1}
            >
              {gym.name}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: SPACE.xs,
    paddingVertical: SPACE.xs,
  },
  pill: {
    paddingVertical: SPACE.xs + 2,
    paddingHorizontal: SPACE.base,
    borderRadius: RADIUS.xs,
    borderWidth: 1,
  },
  pillText: {
    fontSize: FONT.sm,
    fontFamily: TYPO.subheading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wide,
  },
});
